import {
  AfterViewInit,
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  forwardRef,
  input,
  Input,
  OnChanges,
  Output,
  signal,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { SelectModule } from 'primeng/select';
import {
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
  FormsModule
} from '@angular/forms';
import { MultiSelectModule } from 'primeng/multiselect';
import { ShadowDomStyleInjectorService } from '@everymatrix-base-components/utils';
import { CommonModule, NgStyle } from '@angular/common';
import { Checkbox } from "primeng/checkbox";
import { CheckboxModule } from 'primeng/checkbox';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

@Component({
  selector: 'lib-dm-select-lib',
  imports: [SelectModule, MultiSelectModule, ReactiveFormsModule, NgStyle, CommonModule, Checkbox, CheckboxModule, FormsModule, ProgressSpinnerModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DmSelectLibComponent),
      multi: true,
    },
  ],
  templateUrl: './dm-select-lib.component.html',
  styleUrl: './dm-select-lib.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.ShadowDom,
})
export class DmSelectLibComponent implements OnChanges, AfterViewInit {
  /**
   * Label text displayed above the input
   */
  @Input() label = '';

  /**
   * Placeholder text inside the input
   */
  @Input() placeholder = '';

  /**
   * Disables the input field if set to true
   */
  @Input({ transform: booleanAttribute }) isDisabled = false;

  /**
   * Marks the input as required if set to true
   */
  @Input({ transform: booleanAttribute }) isRequired = false;

  /**
   * Determines whether to use multiple selection
   */
  @Input({ transform: booleanAttribute }) isMultiSelect = false;

  /**
   * When specified, displays an input field to filter the items on keyup.
   */
  @Input({ transform: booleanAttribute }) filter = true;

  /**
   * Initial value provided by the parent component
   * Usage: pass a simple string (key value) for single select and for multiselect pass a stringified array of key values.
   */
  @Input() set value(data: string) {
    if (data && JSON.stringify(this.selectControl.value) !== data) {
      let parsedValue: string | string[];
      try {
        parsedValue = JSON.parse(data);
      } catch (e) {
        parsedValue = data;
      }
      this.selectControl.setValue(parsedValue);
    }
  }

  public allRawOptions = signal<any[]>([]);
  public formValueSignal = signal<any[]>([]);

  /**
   * An array of objects to display as the available options.
   * Usage:
   * - Pass an array of objects directly, for example:
   *   [
   *     { label: 'Test 1', value: 'test1' },
   *     { label: 'Test 2', value: 'test2' },
   *     { label: 'Test 3', value: 'test3' },
   *     // ...
   *   ]
   *
   * - Alternatively, pass a JSON stringified array of objects, for example:
   *   '[{"label":"Test 1","value":"test1"},{"label":"Test 2","value":"test2"},{"label":"Test 3","value":"test3"}]'
   */
  @Input() set options(value: any) {
    let parsed = value;
    if (value && typeof value === 'string') {
      parsed = JSON.parse(value);
    }
    this.selectOptions = Array.isArray(parsed) ? [...parsed] : [];
    this.allRawOptions.set(Array.isArray(parsed) ? [...parsed] : []);
  }

  /**
   * Name of the value field of an option.
   */
  @Input() optionValue = 'value';

  /**
   * Name of the label field of an option.
   */
  @Input() optionLabel = 'label';

  /**
   * Name of the disabled field of an option.
   */
  @Input() optionDisabled = '';

  /**
   * Text to display when filtering does not return any results. Defaults to global value in i18n translation configuration.
   */
  @Input() emptyFilterMessage = '';

  /**
   * Filter placeholder
   */
  @Input() filterPlaceHolder = 'Search';

  /**
   * Filter by field
   */
  @Input() filterBy!: string;

  /**
   * Loading state
   */
  @Input() loading = false;

  /**
   * Hint message
   */
  hint = input<string>('');

  /**
   * Always show hint
   */
  permanentHint = input<boolean>(false);
  selectOptions!: any[];
  selectedItemsLabel!: string;
  selectControl: FormControl<any> = new FormControl<any>('');
  isDropdownOpen = false;
  isFocused = false;
  @Input({ transform: booleanAttribute }) showClear = true;
  attachedToShadowDom = signal(false);
  @Input({ transform: booleanAttribute }) appendToBody = false;
  @Input({ transform: booleanAttribute }) virtualScroll = false;
  @Input({ transform: booleanAttribute }) isGrouped = false;
  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

   /**
   * Emits when dropdown is opened
   */
  @Output() openState: EventEmitter<void> = new EventEmitter();
  /**
   * Emits when selection changed
   */
  @Output() onSelectChange: EventEmitter<void> = new EventEmitter();

  constructor(
    private elementRef: ElementRef,
    private shadowDomStyleInjectorService: ShadowDomStyleInjectorService
  ) {
    this.selectControl.valueChanges.subscribe((value) => {
      if (this.isMultiSelect) {
        this.updateSelectedLabels(value);
        this.formValueSignal.set(value ?? []); 
      }

      this.onChange(value);
      this.onTouched();
      this.elementRef.nativeElement.dispatchEvent(
        new CustomEvent('dm-select-value-changed', {
          detail: value,
          bubbles: true,
          composed: true,
        })
      );
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isRequired']) {
      if (this.isRequired) {
        this.selectControl.setValidators(Validators.required);
      } else {
        this.selectControl.clearValidators();
      }
      this.selectControl.updateValueAndValidity();
    }

    if (changes['isDisabled']) {
      this.setDisabledState()
    }

  }

  ngAfterViewInit() {
    this.shadowDomStyleInjectorService
      .injectNgPrimeStyles(this.elementRef)
      .subscribe((data) => this.attachedToShadowDom.set(data));
  }

  writeValue(value: any): void {
    this.selectControl.setValue(value);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(): void {
    if (this.isDisabled) {
      this.selectControl.disable({ emitEvent: false });
    } else {
      this.selectControl.enable({ emitEvent: false });
    }
  }

  onClick():void{
    setTimeout(() => {
      this.openState.emit();
    });
  }

  public isAllSelectedSignal = computed(() => {
    const currentValues = this.formValueSignal();
    const rawOptions = this.allRawOptions();
    if (!Array.isArray(currentValues) || !currentValues.length || !Array.isArray(rawOptions) || !rawOptions.length) {
      return false;
    }
    
    if (this.isGrouped) {
      const totalElementsCount = rawOptions.reduce((sum, group) => {
        return sum + (Array.isArray(group.items) ? group.items.length : 0);
      }, 0);
      return currentValues.length === totalElementsCount;
    }
    return currentValues.length === rawOptions.length;
  });

  public getInputValue(event: Event): string {
    const input = event.target as HTMLInputElement;
    return input.value || ''
  }

  public onCustomSearch(value: string): void {
    const text = (value).toLowerCase().trim();
    const raw = this.allRawOptions();

    if (!text) {
      this.selectOptions = [...raw];
      return;
    }

    const filterFields = (this.filterBy ? this.filterBy : this.optionLabel)
      .split(',')
      .map(field => field.trim());

    const matchElement = (opt: any) => {
      return filterFields.some(field => {
        const fieldValue = opt[field];
        return fieldValue !== undefined && fieldValue !== null && 
          String(fieldValue).toLowerCase().includes(text);
      });
    };

    if (this.isGrouped) {
      this.selectOptions = raw
        .map(group => {
          const filteredItems = Array.isArray(group.items) 
            ? group.items.filter((item: any) => matchElement(item)) 
            : [];
          return { ...group, items: filteredItems };
        })
        .filter(group => group.items.length > 0);
    } else {
      this.selectOptions = raw.filter(opt => matchElement(opt));
    }
  }

  public toggleSelectAll(event: any): void {
    const isChecked = typeof event === 'boolean' ? event : event?.checked;

    if (isChecked) {
      const rawOptions = this.allRawOptions();
      let allIds: any[] = [];
      if (this.isGrouped) {
        allIds = rawOptions.reduce((acc, group) => {
          if (Array.isArray(group.items)) {
            const groupIds = group.items.map((item: any) => item[this.optionValue]);
            return acc.concat(groupIds);
          }
          return acc;
        }, []);
      } else {
        allIds = rawOptions.map(opt => opt[this.optionValue]);
      }

      this.selectControl.setValue(allIds);
      this.formValueSignal.set(allIds);
    } else {
      this.selectControl.setValue([]);
      this.formValueSignal.set([]);
    }
    this.onSelectChange.emit();
  }

  private updateSelectedLabels(value: any[] | null | undefined): void {
    const selectedValues = value || [];
    
    if (!selectedValues.length) {
      this.selectedItemsLabel = '';
      return;
    }

    if (selectedValues.length > 3) {
      this.selectedItemsLabel = `Selected ${this.label}: ${selectedValues.length}`;
      return;
    }

    const rawOptions = this.allRawOptions();
    const selectedLabels: string[] = [];

    const selectedSet = new Set(selectedValues.map(id => id));

    if (this.isGrouped) {
      for (const group of rawOptions) {
        if (Array.isArray(group.items)) {
          for (const item of group.items) {
            if (selectedSet.has(item[this.optionValue])) {
              selectedLabels.push(item[this.optionLabel]);
            }
          }
        }
      }
    } else {
      for (const opt of rawOptions) {
        if (selectedSet.has(opt[this.optionValue])) {
          selectedLabels.push(opt[this.optionLabel]);
        }
      }
    }

    this.selectedItemsLabel = selectedLabels.length > 0 ? selectedLabels.join(', ') : `Selected ${this.label}: ${selectedValues.length}`;
  }
}
